import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import "./ContractorProfile.css";
import DatePicker from 'react-datepicker';
import { generateContractorCode } from '../../components/GenerateAttendanceCode';


const ContractorProfile = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData] = useFetch();
  const [userId, setUserId] = useState(authState.user._id);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [userName, setUserName] = useState(authState.user.name);
  const [contractorCode, setContractorCode] = useState(generateContractorCode());

  const [profileData, setProfileData] = useState({
    userCode: userCode,
    userId: userId,
    userName: userName,
    contractorName: '',
    contractorCode: contractorCode,
    ownerName: '',
    fatherHusbandName: '',
    gender: '',
    dob: null,
    location: '',
    address: '',
    city: '',
    state: '',
    country:'',
    pincode: '',
    email: '',
    contactNumber: '',
    natureOfWork: '',
    managerCode: '',
    managerName: '',
    managerDesignation: '',
    managerAddress: '',
    licenseNumber: '',
    licensingOfficerName: '',
    placeOfLicensingOffice: '',
    licenseFeePaid: '',
    securityDeposit: '',
    licenseDate: null,
    licenseExpiryDate: null,
    convictionDetails: '',
    suspensionOrderDetails: '',
    createdBy: userId,
    isSubContractor: false,
    subContractors: [{subContractorCode: '', subContractorName: ''}],
    dateCreated: new Date(),
    isActive: false,
    isDeleted: false
  });

  const [originalProfileData, setOriginalProfileData] = useState({});
  const [profileExists, setProfileExists] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState('');
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserProfile(userId);
  }, [userId]);

  const fetchUserProfile = useCallback(() => {
    try {
      const config = {
        url: "/contractor/byid",
        method: 'post',
        data: { userId: userId },
        headers: { Authorization: authState.token },
      };
      fetchData(config)
        .then(response => {
          // console.log("profile response ",response);

          if (response && response.status) {
            // console.log("profile exists ",response);
            setProfileExists(true);
            const { profile } = response;
            setProfileId(profile._id);
            setProfileData({
              ...profileData,
              ...profile,
            });
          } else {
            setCreateSuccessMsg("Sorry! Contractor Not Found. Please retry.");
          }
        })
        .catch(() => {
          setCreateSuccessMsg("Sorry! Contractor Not Found. Please retry.");
        });
    } catch {
      setCreateSuccessMsg("Sorry! Contractor not found. Please retry.");
    }
  }, [userId, fetchData, setProfileExists]);

  const handleProfileUpdate = useCallback(() => {
    validateInputs();
    const config = {
      url: `/contractor/updateprofile`,
      method: 'post',
      data: { profileData: profileData, userId: userId},
      headers: { Authorization: authState.token },
    };
    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setOriginalProfileData(profileData);
          setUpdateSuccess(true);
          setUpdateSuccessMsg("Contractor updated successfully!");
        } else {
          setUpdateSuccessMsg("Error updating Contractor! Please retry.");
        }
      })
      .catch(() => {
        setUpdateSuccessMsg("Error updating Contractor! Please retry.");
      });
  }, [profileData, profileId, fetchData]);

  const handleCancel = () => {
    setProfileData(originalProfileData);
  };

  const handleCreateProfile = () => {
    setCreatingProfile(true);
    fetchProfileCreate();
  };

  const fetchProfileCreate = useCallback(() => {
    const config = {
      url: "/contractor",
      method: 'post',
      data: profileData,
      headers: { Authorization: authState.token },
    };
    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setCreateSuccess(true);
          setCreateSuccessMsg("Contractor Created Successfully");
        } else {
          setCreateSuccessMsg("Sorry! Contractor Not Created. Please retry.");
        }
      })
      .catch(() => {
        setCreateSuccessMsg("Sorry! Contractor Not Created. Please retry.");
      });
  }, [profileData, fetchData]);

  const validateInputs = () => {
    let isValid = true;
    const errors = {};
    const mobileRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!profileData.contractorName) {
      errors.contractorName = 'Name is required.';
      isValid = false;
    }

    if (!profileData.natureOfWork) {
      errors.natureOfWork = 'Nature of Work is required.';
      isValid = false;
    }

    if (!profileData.address) {
      errors.address = 'Address is required.';
      isValid = false;
    }
    if (!profileData.state) {
      errors.state = 'State is required.';
      isValid = false;
    }
    if (!profileData.city) {
      errors.city = 'City is required.';
      isValid = false;
    }
    if (!profileData.country) {
      errors.country = 'Country is required.';
      isValid = false;
    }

    if (!profileData.pincode) {
      errors.pincode = 'Pincode is required.';
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  return (
    <MainLayout>
      <div className='text-lg mt-12 mb-4 mx-8 border-b border-b-gray-300 text-white'>
        <h2>Contractor Master</h2>
      </div>
      <div className='main-content mt-4 border border-b-gray-300 p-6 rounded-lg bg-gray-800 text-white'>
        {!profileExists && (
          <div className='flex justify-center mt-4'>
            <button
              className='create-profile-button px-4 py-2 bg-blue-600 rounded-full text-white hover:bg-blue-700'
              onClick={handleCreateProfile}
              disabled={creatingProfile}
            >
              Initiate Your Contractor Master Record Creation
            </button>
            {createSuccess && (
              <p className='text-lg text-green-500'>Your Contractor Profile Setup Successfully!! Come back on this page again to setup your details.</p>
            )}
          </div>
        )}
        {profileExists && (
          <>
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col'>
                <label >Contractor Think5Whys Code:</label>
                <input
                  className="profile-input bg-gray-600 rounded-md px-2 py-1"
                  type="text"
                  value={profileData.contractorCode}
                  disabled
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Contrator / Firm Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.contractorName}
                    onChange={(e) => setProfileData({ ...profileData, contractorName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Owner Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.ownerName}
                    onChange={(e) => setProfileData({ ...profileData, ownerName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Father / Husband Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.fatherHusbandName}
                    onChange={(e) => setProfileData({ ...profileData, fatherHusbandName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Owner Gender:</label>
                <select
                  className="profile-select bg-gray-600 rounded-md px-2 py-1"
                  value={profileData.gender}
                  onChange={(e) => setProfileData({ ...profileData, gender: e.target.value })}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && <span className="text-red-500 text-xs">{errors.gender}</span>}
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Date of Birth / Incorporation:</label>
                <input
                  className="profile-date bg-gray-600 rounded-md px-2 py-1"
                  type="date"
                  value={profileData.dob ? profileData.dob.substring(0, 10) : ''}
                  onChange={(e) => setProfileData({ ...profileData, dob: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Email:</label>
                <input
                  className="profile-input bg-gray-600 rounded-md px-2 py-1"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Contact Number:</label>
                <input
                  className="profile-input bg-gray-600 rounded-md px-2 py-1"
                  type="tel"
                  value={profileData.contactNumber}
                  onChange={(e) => setProfileData({ ...profileData, contactNumber: e.target.value })}
                />
                {errors.contactNumber && <span className="text-red-500 text-xs">{errors.contactNumber}</span>}
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Location:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.location}
                    onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>City:</label>
                <input 
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    style={{ border: '1px solid #ccc'}}
                    type="text"
                    value={profileData.city}
                    onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>State:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    style={{ border: '1px solid #ccc' }}
                    value={profileData.state}
                    onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Country:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    style={{ border: '1px solid #ccc' }}
                    value={profileData.country}
                    onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Postal Code:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    style={{
                        border: '1px solid #ccc',
                        width: '100px',
                    }}
                    value={profileData.pincode}
                    onChange={(e) => setProfileData({ ...profileData, pincode: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Address:</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '150px',
                    }}
                    value={profileData.address}
                    onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Nature of Work:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.natureOfWork}
                    onChange={(e) => setProfileData({ ...profileData, natureOfWork: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Manager Think5Whys User Code:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.managerCode}
                    onChange={(e) => setProfileData({ ...profileData, managerCode: e.target.value })}
                />
              </div>


              <div className='flex flex-col'>
                <label className='text-yellow-200'>Manager Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.managerName}
                    onChange={(e) => setProfileData({ ...profileData, managerName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Manager Designation:</label>
                <input 
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    style={{ border: '1px solid #ccc'}}
                    type="text"
                    value={profileData.managerDesignation}
                    onChange={(e) => setProfileData({ ...profileData, managerDesignation: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Manager Address:</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '150px',
                    }}
                    value={profileData.managerAddress}
                    onChange={(e) => setProfileData({ ...profileData, managerAddress: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>License Number:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.licenseNumber}
                    onChange={(e) => setProfileData({ ...profileData, licenseNumber: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>License Officer Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.licensingOfficerName}
                    onChange={(e) => setProfileData({ ...profileData, licensingOfficerName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Place of License Officer:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.placeOfLicensingOffice}
                    onChange={(e) => setProfileData({ ...profileData, placeOfLicensingOffice: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>License Date:</label>
                <input
                  className="profile-date bg-gray-600 rounded-md px-2 py-1"
                  type="date"
                  value={profileData.licenseDate ? profileData.licenseDate.substring(0, 10) : ''}
                  onChange={(e) => setProfileData({ ...profileData, licenseDate: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>License Expiry Date:</label>
                <input
                  className="profile-date bg-gray-600 rounded-md px-2 py-1"
                  type="date"
                  value={profileData.licenseExpiryDate ? profileData.licenseExpiryDate.substring(0, 10) : ''}
                  onChange={(e) => setProfileData({ ...profileData, licenseExpiryDate: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>License Fee Paid:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.licenseFeePaid}
                    onChange={(e) => setProfileData({ ...profileData, licenseFeePaid: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Security Deposit:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.securityDeposit}
                    onChange={(e) => setProfileData({ ...profileData, securityDeposit: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Conviction Details (If any):</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '150px',
                    }}
                    value={profileData.convictionDetails}
                    onChange={(e) => setProfileData({ ...profileData, convictionDetails: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Suspension Details (If any):</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '150px',
                    }}
                    value={profileData.suspensionOrderDetails}
                    onChange={(e) => setProfileData({ ...profileData, suspensionOrderDetails: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Remarks:</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '100px',
                    }}
                    value={profileData.remarks}
                    onChange={(e) => setProfileData({ ...profileData, remarks: e.target.value })}
                />
              </div>

              <p className='text-yellow-200 mb-4'>
                  Label/Information in yellow color will be displayed on your Contractor Digital Card
              </p>

              <div className='flex justify-center'>
                <button className='ml-4 text-sm' onClick={handleCancel} disabled={true}>
                    Our Past Works
                </button>
                <button className='ml-4 text-sm' onClick={handleCancel} disabled={true}>
                    Our Customers
                </button>
                <button className='ml-4 text-sm' onClick={handleCancel} disabled={true}>
                    Our Expertise
                </button>
                <button className='ml-4 text-sm' onClick={handleCancel} disabled={true}>
                    How We Do?
                </button>
              </div>
            </div>
            {updateSuccess && (
              <div className="success-message text-green-500 text-lg mt-4">
                {updateSuccessMsg}
              </div>
            )}
            <div className='flex mt-6'>
              <button
                className='update-profile-button px-4 py-2 bg-green-600 rounded-full text-white hover:bg-green-700 w-40'
                onClick={handleProfileUpdate}
              >
                Update
              </button>
              <button
                className='update-profile-button px-4 py-2 bg-red-600 rounded-full text-white hover:bg-red-700 w-40'
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </>
        )}

        {!profileExists && createSuccess && (
          <div className="flex flex-col justify-center success-message text-green-500 text-xl mt-4">
            {createSuccessMsg}
            <button
                className='update-profile-button px-4 py-2 bg-red-600 rounded-full text-white hover:bg-red-700 w-40'
                onClick={() => navigate("/think-contract-contractor")}
              >
                OK
            </button>
          </div>
        )}
     </div>
    </MainLayout>
  );
};

export default ContractorProfile;



